import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { selectDeviceType } from 'features/application/applicationSlice';
import { ContentPanel } from 'features/content-library/components/ContentPanel/ContentPanel';
import {
  selectGridPanel,
  selectGridPanelMaximized,
  selectMaximizedContent,
  selectMaximizedStream,
  selectShowGridPanelTabs,
} from 'features/layout/features/content/contentSlice';
import { GridPanelName } from 'features/layout/features/content/types';
import {
  contentWidthChanged,
  selectContentWidth,
} from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { GridPanelControls } from 'features/layout/grid-panel/GridPanelControls';
import { GridPanelTabs } from 'features/layout/grid-panel/GridPanelTabs';
import { useGridPanelDragHandle } from 'features/layout/grid-panel/useGridPanelDragHandle';
import { useResizeDragGuard } from 'features/layout/useResizeDragGuard';
import { ScreensharePanel } from 'features/screenshare/components/ScreensharePanel';
import { ExternalStreamPanel } from 'features/layout/layouts/ExternalStreamPanel';
import { hoverParentStyles, useHoverParent } from 'features/layout/useHoverParent';
import { WhiteboardPanel } from 'features/whiteboard/WhiteboardPanel';
import {
  selectCurrentLayoutMode,
  selectCustomTilesCount,
} from 'features/layout/features/config/configSlice';
import { selectHideTilesInRestreaming } from 'features/layout/features/config/systemSessionSelectors';
import { selectPageStreams } from 'features/layout/features/pagination/paginationSlice';
import { LayoutMode } from 'features/layout/types';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { readPipOuterWidth } from 'components/PiP/readPipOuterWidth';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { ForwardedRef, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { Axis, Resizable, ResizeCallbackData } from 'react-resizable';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { layoutConfig } from 'utils/layout';
import cx from 'clsx';
import { ContentWhiteboardProvider } from 'features/content-whiteboard/context/ContentWhiteboardContext';
import {
  selectContentLibraryOpenedFile,
  selectIsPreviewActive,
} from 'features/content-library/contentLibrarySlice';
import { isMultiPageFile } from 'features/content-library/utils/isMultiPageFile';
import { PreviewPanel } from 'features/content-library/components/PreviewPanel/PreviewPanel';
import { getPinnedContentBounds } from 'features/layout/getPinnedContentBounds';

const Panel = styled(Box)(({ theme }) => ({
  height: '100%',
  position: 'relative',
  padding: `${theme.room.tileGap}px`,
}));

const HandleOuter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show?: boolean }>(({ theme, show }) => ({
  height: '100%',
  width: '10px',
  position: 'absolute',
  right: 0,
  top: 0,
  display: show ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'ew-resize',
  zIndex: 2,
  '.resize-pill': {
    opacity: 1,
    height: '36px',
    width: '6px',
    borderRadius: '4px',
    border: `1px solid ${theme.room.tileBackground}`,
    backgroundColor: '#fff',
  },
}));

const PanelInner = styled('div')({
  height: '100%',
  ...hoverParentStyles,
});

const CustomHandle = React.forwardRef(
  (props: Partial<{ handleAxis: Axis; show: boolean }>, ref: ForwardedRef<HTMLDivElement>) => {
    const { handleAxis, show, ...restProps } = props;
    return (
      <HandleOuter ref={ref} className={`handle-${handleAxis}`} show={show} {...restProps}>
        <Box className="resize-pill" />
      </HandleOuter>
    );
  }
);

type PanelSize = { width?: number; height?: number };

const panelMap: Record<GridPanelName, (props: { size: PanelSize }) => ReactElement | null> = {
  whiteboard: () => <WhiteboardPanel />,
  contentLibrary: ({ size }) => <ContentPanel size={size} />,
  screenshare: () => <ScreensharePanel />,
  external: () => <ExternalStreamPanel />,
};

interface GridPanelProps {
  roomSize: {
    width?: number;
    height?: number;
  };
}

export const GridPanel = ({
  roomSize: { width: roomWidth, height: roomHeight },
}: GridPanelProps) => {
  const dispatch = useAppDispatch();

  const containerWidth = roomWidth || window.innerWidth;
  const containerHeight = roomHeight || window.innerHeight;
  const maximizedContent = useAppSelector(selectMaximizedContent);
  const contentWidth = useAppSelector(selectContentWidth);
  const gridPanelMaximized = useAppSelector(selectGridPanelMaximized);
  const currentLayoutMode = useAppSelector(selectCurrentLayoutMode);

  const defaultPanelWidth = containerWidth * layoutConfig.defaultPinWidth;

  const hasDragHandle = useGridPanelDragHandle();

  const activePanel = useAppSelector(selectGridPanel);
  const showTabs = useAppSelector(selectShowGridPanelTabs);
  const isPreviewActive = useAppSelector(selectIsPreviewActive);

  const { isMobile } = useBreakpoints();
  const { pipWindow } = usePiPWindow();

  const hideTilesInRestreaming = useAppSelector(selectHideTilesInRestreaming);

  const streams = useAppSelector(selectPageStreams);
  const maximizedStream = useAppSelector(selectMaximizedStream);

  const customTilesCount = useAppSelector(selectCustomTilesCount);

  const pipOuterWidth = readPipOuterWidth(pipWindow);

  const maximizeGridPanel =
    (gridPanelMaximized && currentLayoutMode === LayoutMode.tiled) ||
    isMobile ||
    (pipOuterWidth !== null && pipOuterWidth <= 450) ||
    (!streams.length && !customTilesCount && !maximizedStream) ||
    hideTilesInRestreaming;

  const deviceType = useAppSelector(selectDeviceType);

  const { tilesReserve, maxContentWidth, minPanelWidth } = getPinnedContentBounds({
    containerWidth,
    hasTiles: streams.length + customTilesCount > 0,
    isPiP: !!pipWindow,
  });

  const width = useMemo(
    () =>
      currentLayoutMode === LayoutMode.auto
        ? Math.min(defaultPanelWidth, maxContentWidth)
        : Math.min(contentWidth * containerWidth, maxContentWidth),
    [contentWidth, containerWidth, maxContentWidth, currentLayoutMode, defaultPanelWidth]
  );

  const onResize = useCallback(
    (e: React.SyntheticEvent, { size: { width: newWidth } }: ResizeCallbackData) => {
      dispatch(contentWidthChanged(newWidth / containerWidth));
    },
    [containerWidth, dispatch]
  );

  const { resizeGuardSx, onResizeStart, onResizeStop } = useResizeDragGuard();

  useEffect(() => {
    // Only enforce the lower bound: bump content up when it would render below its minimum. Do NOT
    // write the upper (maxContentWidth) clamp back into contentWidth. Persisting the clamp drifts the
    // user's chosen width down whenever the container narrows (side panel opens) and, during a live
    // window resize, the autobatched write-back oscillates content across the clamp boundary, making
    // the tile strip flip between one and two columns (SAMBA-9138). The upper clamp lives in `width`,
    // applied for rendering only.
    if (contentWidth === 1) {
      return;
    }

    const minPercent = minPanelWidth / containerWidth;

    if (contentWidth < minPercent - 0.001) {
      dispatch(contentWidthChanged(minPercent));
    }
  }, [containerWidth, contentWidth, minPanelWidth, dispatch]);

  const openedFile = useAppSelector(selectContentLibraryOpenedFile);
  const effectivePanelWidth = maximizeGridPanel ? containerWidth : width;
  const splitBottomControls = effectivePanelWidth < 540 && isMultiPageFile(openedFile);

  const getCssPanelWidth = () => {
    if (currentLayoutMode === LayoutMode.auto) {
      return `min(${layoutConfig.defaultPinWidth * 100}%, calc(100% - ${tilesReserve}px))`;
    }

    if (contentWidth === 1) {
      return '100%';
    }

    return `min(${contentWidth * 100}%, calc(100% - ${tilesReserve}px))`;
  };

  const cssPanelWidth = getCssPanelWidth();

  const { className, onMouseEnter, onMouseMove, onMouseLeave } = useHoverParent('gridPanel');
  const mouseCallbacks = { onMouseEnter, onMouseMove, onMouseLeave };

  if ((!activePanel && !isPreviewActive) || (maximizedContent === 'stream' && contentWidth === 1)) {
    return null;
  }

  if (!(containerWidth && containerHeight)) {
    return null;
  }

  const ActivePanel = activePanel ? panelMap[activePanel] : null;

  const size = { width: containerWidth, height: containerHeight - 41 };

  return (
    <ContentWhiteboardProvider>
      <Resizable
        width={width}
        height={containerHeight}
        onResize={onResize}
        onResizeStart={onResizeStart}
        onResizeStop={onResizeStop}
        maxConstraints={[maxContentWidth, containerHeight]}
        minConstraints={[minPanelWidth, 0]}
        resizeHandles={deviceType === 'mobile' || isMobile ? [] : ['e']}
        axis="x"
        handle={<CustomHandle show={hasDragHandle} />}
      >
        <Panel
          className={cx(className, { 'fullscreen-panel': maximizeGridPanel }, 'grid-panel')}
          sx={(theme) => ({
            ...(maximizeGridPanel
              ? {}
              : {
                  minWidth: cssPanelWidth,
                }),
            paddingRight: hasDragHandle
              ? `${theme.room.tileGap * 4 - 2}px`
              : `${theme.room.tileGap}px`,
            ...resizeGuardSx,
          })}
          {...mouseCallbacks}
        >
          <PanelInner>
            {showTabs && <GridPanelTabs panelWidth={width} />}
            <Box
              sx={{
                height: showTabs ? 'calc(100% - 40px)' : '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              {isPreviewActive ? (
                <PreviewPanel size={size} />
              ) : (
                ActivePanel && <ActivePanel size={size} />
              )}
            </Box>
            <GridPanelControls
              activePanel={activePanel}
              splitBottomControls={splitBottomControls}
              panelWidth={effectivePanelWidth}
            />
          </PanelInner>
        </Panel>
      </Resizable>
    </ContentWhiteboardProvider>
  );
};
